<template>
    <div class="min-h-screen bg-background">
      <div class="container mx-auto px-4 py-8">
        <!-- Header -->
        <div class="flex items-center justify-between mb-8">
          <div class="flex items-center space-x-4">
            <button
              @click="$router.push('/cart')"
              class="p-2 text-foreground hover:text-secondary transition-colors"
            >
              <svg class="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/>
              </svg>
            </button>
            <h1 class="text-3xl font-bold text-foreground">Commande</h1>
          </div>
          <div class="text-right">
            <p class="text-sm text-muted-foreground">Total</p>
            <p class="text-2xl font-bold text-foreground">{{ total.toFixed(2) }} XAF</p>
          </div>
        </div>

        <!-- Progress Steps -->
        <div class="flex items-center  mb-8 overflow-x-auto">
          <div v-for="(step, index) in steps" :key="step.number" class="flex items-center">
            <div :class="`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              currentStep >= step.number
                ? 'bg-primary border-primary text-white'
                : 'border-gray-300 text-gray-500'
            }`">
              {{ step.number }}
            </div>
            <div class="ml-2 hidden sm:block">
              <p :class="`text-sm font-medium ${
                currentStep >= step.number ? 'text-primary' : 'text-gray-500'
              }`">
                {{ step.title }}
              </p>
            </div>
            <div v-if="index < steps.length - 1" :class="`w-12 h-0.5 mx-4 ${
              currentStep > step.number ? 'bg-primary' : 'bg-gray-300'
            }`" />
          </div>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <!-- Form -->
          <div class="lg:col-span-2">
            <div class="bg-card border border-border rounded-lg shadow-md">
              <div class="p-6">
                <form @submit.prevent="handleSubmit">
                  <!-- Step 1: Personal Information -->
                  <div v-if="currentStep === 1" class="space-y-6">
                    <h2 class="text-xl font-semibold mb-6">Informations personnelles</h2>
                    
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label for="firstName" class="block text-sm font-medium text-foreground mb-2">Prénom *</label>
                        <input
                          id="firstName"
                          v-model="formData.first_name"
                          @input="updateFormData('first_name', $event.target.value)"
                          :class="`w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                            errors.first_name ? 'border-destructive' : 'border-input'
                          }`"
                        />
                        <p v-if="errors.first_name" class="text-sm text-destructive mt-1">{{ errors.first_name }}</p>
                      </div>
                      
                      <div>
                        <label for="lastName" class="block text-sm font-medium text-foreground mb-2">Nom *</label>
                        <input
                          id="lastName"
                          v-model="formData.last_name"
                          @input="updateFormData('last_name', $event.target.value)"
                          :class="`w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                            errors.last_name ? 'border-destructive' : 'border-input'
                          }`"
                        />
                        <p v-if="errors.last_name" class="text-sm text-destructive mt-1">{{ errors.last_name }}</p>
                      </div>
                    </div>

                    <div>
                      <label for="email" class="block text-sm font-medium text-foreground mb-2">Email *</label>
                      <input
                        id="email"
                        type="email"
                        v-model="formData.email"
                        @input="updateFormData('email', $event.target.value)"
                        :class="`w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                          errors.email ? 'border-destructive' : 'border-input'
                        }`"
                      />
                      <p v-if="errors.email" class="text-sm text-destructive mt-1">{{ errors.email }}</p>
                    </div>

                    <div>
                      <label for="phone" class="block text-sm font-medium text-foreground mb-2">Téléphone *</label>
                      <input
                        id="phone"
                        type="tel"
                        v-model="formData.phone"
                        @input="updateFormData('phone', $event.target.value)"
                        :class="`w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                          errors.phone ? 'border-destructive' : 'border-input'
                        }`"
                      />
                      <p v-if="errors.phone" class="text-sm text-destructive mt-1">{{ errors.phone }}</p>
                    </div>
                  </div>

                  <!-- Step 2: Shipping Address -->
                  <div v-else-if="currentStep === 2" class="space-y-6">
                    <h2 class="text-xl font-semibold mb-6">Adresse de livraison</h2>
                    
                    <div>
                      <label for="address" class="block text-sm font-medium text-foreground mb-2">Adresse *</label>
                      <input
                        id="address"
                        v-model="formData.address"
                        @input="updateFormData('address', $event.target.value)"
                        :class="`w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                          errors.address ? 'border-destructive' : 'border-input'
                        }`"
                      />
                      <p v-if="errors.address" class="text-sm text-destructive mt-1">{{ errors.address }}</p>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label for="city" class="block text-sm font-medium text-foreground mb-2">Ville *</label>
                        <input
                          id="city"
                          v-model="formData.city"
                          @input="updateFormData('city', $event.target.value)"
                          :class="`w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                            errors.city ? 'border-destructive' : 'border-input'
                          }`"
                        />
                        <p v-if="errors.city" class="text-sm text-destructive mt-1">{{ errors.city }}</p>
                      </div>
                      
                      <!-- <div>
                        <label for="postalCode" class="block text-sm font-medium text-foreground mb-2">Code postal *</label>
                        <input
                          id="postalCode"
                          v-model="formData.postal_code"
                          @input="updateFormData('postal_code', $event.target.value)"
                          :class="`w-full px-3 py-2 border rounded-md bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent ${
                            errors.postal_code ? 'border-destructive' : 'border-input'
                          }`"
                        />
                        <p v-if="errors.postal_code" class="text-sm text-destructive mt-1">{{ errors.postal_code }}</p>
                      </div> -->
                    </div>

                    <div>
                      <label for="country" class="block text-sm font-medium text-foreground mb-2">Pays</label>
                      <input
                      type="text"
                        id="country"
                        v-model="formData.country"
                        @input="updateFormData('country', $event.target.value)"
                        class="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      />
                        
                     
                    </div>
                  </div>

                  <!-- Step 3: Delivery Method -->
                  <div v-else-if="currentStep === 3" class="space-y-6">
                    <h2 class="text-xl font-semibold mb-6">Mode de livraison</h2>
                    
                    <div class="space-y-3">
                      <div class="flex items-center space-x-2 p-4 border rounded-lg">
                        <input
                          type="radio"
                          id="standard"
                          v-model="formData.delivery_method"
                          value="standard"
                          class="text-primary focus:ring-primary"
                        />
                        <label for="standard" class="flex-1 cursor-pointer">
                          <div class="flex justify-between items-center">
                            <div>
                              <p class="font-medium">Livraison standard</p>
                              <p class="text-sm text-muted-foreground">3-5 jours ouvrés</p>
                            </div>
                            <span class="font-medium">
                              {{ subtotal > 10000 ? 'Gratuite' : '2000 XAF' }}
                            </span>
                          </div>
                        </label>
                      </div>
                      
                      <div class="flex items-center space-x-2 p-4 border rounded-lg">
                        <input
                          type="radio"
                          id="express"
                          v-model="formData.delivery_method"
                          value="express"
                          class="text-primary focus:ring-primary"
                        />
                        <label for="express" class="flex-1 cursor-pointer">
                          <div class="flex justify-between items-center">
                            <div>
                              <p class="font-medium">Livraison express</p>
                              <p class="text-sm text-muted-foreground">24-48h</p>
                            </div>
                            <span class="font-medium">5000 XAF</span>
                          </div>
                        </label>
                      </div>
                    </div>
                  </div>

                  <!-- Step 4: Payment -->
                  <div v-else-if="currentStep === 4" class="space-y-6">
                    <h2 class="text-xl font-semibold mb-6">Paiement</h2>
                    
                    <div class="space-y-3">
                      <div class="flex items-center space-x-2 p-4 border rounded-lg">
                        <input
                          type="radio"
                          id="card"
                          v-model="formData.payment_method"
                          value="card"
                          class="text-primary focus:ring-primary"
                        />
                        <label for="card" class="flex-1 cursor-pointer">
                          <div class="flex items-center space-x-3">
                            <svg class="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                            </svg>
                            <span>Carte bancaire</span>
                          </div>
                        </label>
                      </div>
                      
                      <div class="flex items-center space-x-2 p-4 border rounded-lg">
                        <input
                          type="radio"
                          id="mobile"
                          v-model="formData.payment_method"
                          value="mobile_money"
                          class="text-primary focus:ring-primary"
                        />
                        <label for="mobile" class="flex-1 cursor-pointer">
                          <div class="flex items-center space-x-3">
                            <svg class="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                            </svg>
                            <span>Mobile Money</span>
                          </div>
                        </label>
                      </div>
                      
                      <div class="flex items-center space-x-2 p-4 border rounded-lg">
                        <input
                          type="radio"
                          id="campay"
                          v-model="formData.payment_method"
                          value="campay"
                          class="text-primary focus:ring-primary"
                        />
                        <label for="campay" class="flex-1 cursor-pointer">
                          <div class="flex items-center space-x-3">
                            <svg class="h-5 w-5 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                            </svg>
                            <span>Campay</span>
                          </div>
                        </label>
                      </div>
                    </div>

                    <div class="space-y-4">
                      <div class="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="terms"
                          v-model="formData.accept_terms"
                          @change="updateFormData('accept_terms', $event.target.checked)"
                          class="rounded border-input bg-background text-primary focus:ring-primary"
                        />
                        <label for="terms" class="text-sm cursor-pointer">
                          J'accepte les{' '}
                          <a href="/terms" class="text-secondary hover:underline">
                            conditions générales de vente
                          </a>{' '}
                          et la{' '}
                          <a href="/privacy" class="text-secondary hover:underline">
                            politique de confidentialité
                          </a>
                        </label>
                      </div>
                      <p v-if="errors.accept_terms" class="text-sm text-destructive">{{ errors.accept_terms }}</p>

                      <div class="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="newsletter"
                          v-model="formData.newsletter"
                          @change="updateFormData('newsletter', $event.target.checked)"
                          class="rounded border-input bg-background text-primary focus:ring-primary"
                        />
                        <label for="newsletter" class="text-sm cursor-pointer">
                          Je souhaite recevoir les offres et actualités par email
                        </label>
                      </div>
                    </div>

                    <div class="flex items-center space-x-2 p-4 bg-muted rounded-lg">
                      <svg class="h-5 w-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
                      </svg>
                      <span class="text-sm">
                        Paiement sécurisé SSL - Vos données sont protégées
                      </span>
                    </div>
                  </div>

                  <!-- Navigation Buttons -->
                  <div class="flex justify-between mt-8">
                    <button v-if="currentStep > 1" type="button" @click="handlePrevious" class="border border-input bg-background hover:bg-accent hover:text-accent-foreground py-2 px-4 rounded-md transition-colors">
                      Précédent
                    </button>
                    
                    <div class="ml-auto">
                      <button v-if="currentStep < 4" type="button" @click="handleNext" :disabled="loading" class="bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-2 px-4 rounded-md transition-colors disabled:opacity-50">
                        <span v-if="loading">Validation...</span>
                        <span v-else>Suivant</span>
                      </button>
                      <button v-else type="submit" :disabled="loading || !cartItems.length" class="bg-primary hover:bg-primary-hover text-primary-foreground font-semibold py-3 px-6 rounded-md transition-colors min-w-[200px] disabled:opacity-50">
                        <span v-if="loading">Traitement...</span>
                        <span v-else>Valider la commande</span>
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          <!-- Order Summary -->
          <div class="lg:col-span-1">
            <div class="bg-card border border-border rounded-lg shadow-md sticky top-20">
              <div class="p-6">
                <h3 class="text-lg font-semibold mb-4">Récapitulatif</h3>
                
                <div v-if="cartItems.length === 0" class="text-center py-4">
                  <p class="text-muted-foreground">Votre panier est vide</p>
                </div>
                
                <div v-else class="space-y-4">
                  <div v-for="item in cartItems" :key="item.id" class="flex justify-between items-start">
                    <div class="flex-1">
                      <p class="font-medium text-sm">{{ item.name || item.product?.name }}</p>
                      <p class="text-xs text-muted-foreground">
                        Taille: {{ item.size || 'Standard' }}
                      </p>
                      <p v-if="item.color" class="text-xs text-muted-foreground">
                        Couleur: {{ item.color }}
                      </p>
                      <p class="text-xs text-muted-foreground">
                        Qté: {{ item.quantity }}
                      </p>
                    </div>
                    <span class="text-sm font-medium">
                      {{ ((item.price || item.product?.price) * item.quantity).toFixed(2) }} XAF
                    </span>
                  </div>
                </div>

                <hr class="my-4 border-border">

                <div class="space-y-2">
                  <div class="flex justify-between">
                    <span>Sous-total</span>
                    <span>{{ cartSubtotal.toFixed(2) }} XAF</span>
                  </div>
                  <div class="flex justify-between">
                    <span>Livraison</span>
                    <span>
                      {{ deliveryFee === 0 ? 'Gratuite' : `${deliveryFee.toFixed(2)} XAF` }}
                    </span>
                  </div>
                </div>

                <hr class="my-4 border-border">

                <div class="flex justify-between text-lg font-bold">
                  <span>Total</span>
                  <span>{{ total.toFixed(2) }} XAF</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  name: 'CheckoutPage',
  
  data() {
    return {
      currentStep: 1,
      loading: false,
      formData: {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'Cameroun',
        delivery_method: 'standard',
        payment_method: 'mobile_money',
        accept_terms: false,
        newsletter: false,
        notes: ''
      },
      errors: {},
      steps: [
        { number: 1, title: 'Informations personnelles' },
        { number: 2, title: 'Adresse de livraison' },
        { number: 3, title: 'Mode de livraison' },
        { number: 4, title: 'Paiement' }
      ]
    }
  },
  
  computed: {
    ...mapState(['cart', 'user']),
    ...mapGetters(['cartTotal']),
    
    cartItems() {
      return this.cart || []
    },
    
    cartSubtotal() {
      return this.cartTotal || 0
    },
    
    deliveryFee() {
      if (this.formData.delivery_method === 'express') {
        return 5000 // XAF pour livraison express
      }
      return this.cartSubtotal > 10000 ? 0 : 2000 // Gratuit au-dessus de 10,000 XAF
    },
    
    total() {
      return this.cartSubtotal + this.deliveryFee
    }
  },
  
 async  mounted() {
    // Initialiser les données utilisateur si connecté
    if (this.user) {
      this.populateUserData()
    }
    
    // Rediriger si le panier est vide
    if (!this.cartItems.length) {
      this.$router.push('/cart')
    }
  },
  
  methods: {
    ...mapActions(['placeOrder', 'fetchUserData']),
    
    populateUserData() {
      // Pré-remplir avec les données utilisateur
      this.formData.first_name = this.user.name || ''
      this.formData.last_name = this.user.prenom || ''
      this.formData.email = this.user.email || ''
      this.formData.phone = this.user.phone || ''
      this.formData.address = this.user.address || ''
      this.formData.city = this.user.city || ''
      this.formData.postal_code = this.user.postal_code || ''
      this.formData.country = this.user.country || 'Cameroun'
    },
    
    validateStep(step) {
      const newErrors = {}

      if (step === 1) {
        if (!this.formData.first_name.trim()) newErrors.first_name = 'Prénom requis'
        if (!this.formData.last_name.trim()) newErrors.last_name = 'Nom requis'
        if (!this.formData.email.trim()) newErrors.email = 'Email requis'
        else if (!/\S+@\S+\.\S+/.test(this.formData.email)) newErrors.email = 'Email invalide'
        if (!this.formData.phone.trim()) newErrors.phone = 'Téléphone requis'
        else if (!/^[0-9+\-\s]{8,15}$/.test(this.formData.phone)) newErrors.phone = 'Numéro invalide'
      }

      if (step === 2) {
        if (!this.formData.address.trim()) newErrors.address = 'Adresse requise'
        if (!this.formData.city.trim()) newErrors.city = 'Ville requise'
        // if (!this.formData.postal_code.trim()) newErrors.postal_code = 'Code postal requis'
      }

      if (step === 4) {
        if (!this.formData.accept_terms) newErrors.accept_terms = 'Vous devez accepter les conditions'
        if (!this.formData.payment_method) newErrors.payment_method = 'Veuillez sélectionner un mode de paiement'
      }

      this.errors = newErrors
      return Object.keys(newErrors).length === 0
    },
    
    async handleNext() {
      if (this.validateStep(this.currentStep)) {
        this.currentStep++
      }
    },
    
    handlePrevious() {
      this.currentStep--
    },
    
    async handleSubmit() {
  if (!this.validateStep(4)) {
    return;
  }
  
  this.loading = true;
  
  try {
    // Calculer le sous-total depuis le panier
    const cartSubtotal = this.cartItems.reduce((total, item) => {
      const price = item.price || item.product?.price || 0;
      return total + (price * item.quantity);
    }, 0);
    
    // Calculer les frais de livraison
    const deliveryFee = this.formData.delivery_method === 'express' ? 5000 : cartSubtotal > 10000 ? 0 : 2000;
    
    // Calculer le total
    const totalAmount = cartSubtotal + deliveryFee;
    
    // Préparer les données cart_items avec les bonnes informations
    const cartItems = this.cartItems.map(item => ({
      product_id: item.product_id || item.id || item.product?.id,
      quantity: item.quantity,
      size: item.size || null,
      color: item.color || null,
      // Note: Le prix est récupéré côté serveur depuis la base de données
    }));
    
    // Préparer les données de commande
    const orderData = {
      first_name: this.formData.first_name,
      last_name: this.formData.last_name,
      email: this.formData.email,
      phone: this.formData.phone,
      address: this.formData.address,
      city: this.formData.city,
      postal_code: this.formData.postal_code,
      country: this.formData.country,
      delivery_method: this.formData.delivery_method,
      payment_method: this.formData.payment_method,
      accept_terms: this.formData.accept_terms,
      newsletter: this.formData.newsletter,
      notes: this.formData.notes,
      total_amount: totalAmount,
      delivery_fee: deliveryFee,
      cart_items: cartItems
    };
    
    // console.log('Données envoyées:', orderData); // Pour débogage
    
    // Appeler l'action du store
    const result = await this.placeOrder(orderData);
    
    // Rediriger vers la page de confirmation
    this.$router.push({
      path: '/order-confirmation',
      query: { 
        orderId: result.order.id,
        orderNumber: result.order.order_number,
        total: totalAmount
      }
    });
    
  } catch (error) {
    console.error('Erreur lors de la commande:', error);
    
    if (error.response?.status === 422) {
      const validationErrors = error.response.data.errors || {};
      this.errors = validationErrors;
      
      // Revenir à l'étape correspondante
      if (validationErrors.first_name || validationErrors.last_name || validationErrors.email || validationErrors.phone) {
        this.currentStep = 1;
      } else if (validationErrors.address || validationErrors.city || validationErrors.postal_code) {
        this.currentStep = 2;
      } else if (validationErrors.delivery_method) {
        this.currentStep = 3;
      } else if (validationErrors.payment_method || validationErrors.accept_terms) {
        this.currentStep = 4;
      }
      
      this.$toast.error('Veuillez corriger les erreurs dans le formulaire');
    } else if (error.response?.status === 400) {
      // Erreurs de stock ou autres
      const errorMessages = error.response.data.errors || [];
      this.$toast.error(errorMessages.join(', ') || 'Erreur lors de la validation de la commande');
    } else if (error.response?.status === 401) {
      this.$toast.error('Veuillez vous connecter pour passer commande');
      this.$router.push('/login?redirect=checkout');
    } else {
      this.$toast.error(error.message || 'Une erreur est survenue lors de la commande');
    }
  } finally {
    this.loading = false;
  }
},
    
    updateFormData(field, value) {
      this.formData[field] = value
      if (this.errors[field]) {
        delete this.errors[field]
      }
    }
  }
}
</script>